import { REPORTS_CONTRACT } from '@shared/broker';
import {
  DamageRuleBillingType,
  DamageRuleCondition,
  IExtendedInspectionInfo,
  IUploadedFileDto,
} from '@shared/services';

import { IReportEstimateSummaryView } from './report';

/**
 * ISO 8601 datetime string.
 *
 * Example: `"2026-02-27T12:34:56.789Z"`
 */
export type TISODateTime = string;

/**
 * Field-selection "include" options for the Report Integration endpoint.
 *
 * Use these values in `?include=...` to request specific blocks of the response payload.
 *
 * Notes:
 * - Some includes are nested (e.g. `media.steps`).
 * - The API Gateway/Report Service may ignore unknown/unsupported includes.
 */
export enum ReportInclude {
  RepInSum = 'summary',
  RepInVeh = 'vehicle',
  RepInNot = 'notes',
  RepInTot = 'totals',
  RepInDam = 'damages',
  RepInMed = 'media',
  RepInMedSte = 'media.steps',
  RepInMedFra360 = 'media.frames360',
  RepInWhe = 'wheels',
  RepInQue = 'questionnaire',
  RepInBef = 'beforeAfter',
  RepInVis = 'visualization',
  RepInExt = 'external',
  RepInEstSum = 'estimateSummary',
}

/**
 * Convenience list of all supported `include` values.
 * Useful for validation (e.g. `IsIn(REPORT_INTEGRATION_INCLUDES)`).
 */
export const REPORT_INTEGRATION_INCLUDES = Object.values(ReportInclude);

/** Type alias for a single include item. */
export type TReportIntegrationInclude = ReportInclude;

/**
 * Field-selection "expand" options for the Report Integration endpoint.
 *
 * Expands are used to request additional derived/enriched data inside already included blocks.
 * Example: `files.signedUrl` can replace raw file paths with signed URLs.
 */
export enum ReportExpand {
  // FilesSignedUrl = 'files.signedUrl',
  CompanyName = 'summary.companyName',
  WhiteLabelMode = 'summary.whiteLabelMode',
  MediaStepsCategory = 'media.steps.category',
  MediaStepsName = 'media.steps.name',
  VisualizationUvMap = 'visualization.uvMap',
  TotalsRequiresAttentionReasons = 'totals.requiresAttention.reasons',
}

/**
 * Convenience list of all supported `expand` values.
 * Useful for validation (e.g. `IsIn(REPORT_INTEGRATION_EXPANDS)`).
 */
export const REPORT_INTEGRATION_EXPANDS = Object.values(ReportExpand);

/** Type alias for a single expand item. */
export type TReportIntegrationExpand = ReportExpand;

/**
 * Query parameters for Report Integration, allowing inclusion and expansion of related entities.
 * @interface IReportIntegrationQuery
 * @property {ReportInclude[]} [include] - List of blocks to include in the response.
 * @property {ReportExpand[]} [expand] - List of expansions to apply to included blocks.
 */
export interface IReportIntegrationQuery {
  include?: ReportInclude[];
  expand?: ReportExpand[];
}

/**
 * Response payload for the Report Integration endpoint.
 *
 * Contains all report-related data blocks, depending on requested `include` and `expand` parameters.
 *
 * @interface IReportIntegrationResponse
 * @property {string} id - Unique identifier for the report.
 * @property {IReportIntegrationSummary | null} [summary] - Summary information about the report.
 * @property {IReportIntegrationVehicle | null} [vehicle] - Vehicle details associated with the report.
 * @property {IReportIntegrationNotes | null} [notes] - Inspection notes.
 * @property {IReportIntegrationMedia} [media] - Media files and related data.
 * @property {IReportIntegrationWheels} [wheels] - Wheel information.
 * @property {IReportIntegrationQuestionnaire} [questionnaire] - Questionnaire answers.
 * @property {IReportIntegrationDamages} [damages] - Damage items.
 * @property {IReportIntegrationVisualization | null} [visualization] - Visualization data, including optional UV-map.
 * @property {IReportIntegrationTotals} [totals] - Totals and summary values.
 * @property {IReportEstimateSummaryView | null} [estimateSummary] - Dealer CR v2 Estimate Summary (only for Dealer/Rental EST reports; null/omitted otherwise).
 * @property {IExtendedInspectionInfo | null} [external] - Extended inspection information from the Inspection Service.
 * @property {IReportIntegrationBeforeAfter | null} [beforeAfter] - Before/after comparison data.
 * @property {IReportIntegrationMeta} [_meta] - Metadata about the response.
 */
export interface IReportIntegrationResponse {
  id: string;

  summary?: IReportIntegrationSummary | null;
  vehicle?: IReportIntegrationVehicle | null;

  notes?: IReportIntegrationNotes | null;

  media?: IReportIntegrationMedia;
  wheels?: IReportIntegrationWheels;
  questionnaire?: IReportIntegrationQuestionnaire;

  damages?: IReportIntegrationDamages;
  visualization?: IReportIntegrationVisualization | null;
  totals?: IReportIntegrationTotals;

  external?: IExtendedInspectionInfo | null;

  beforeAfter?: IReportIntegrationBeforeAfter | null;

  _meta?: IReportIntegrationMeta;
}

/**
 * Summary information about the report.
 * @interface IReportIntegrationSummary
 * @property {number} [companyId] - Identifier for the company.
 * @property {number} [locationId] - Identifier for the location.
 * @property {string} [companyName] - Name of the company.
 * @property {TISODateTime} [completedAt] - ISO 8601 datetime when the report was completed.
 * @property {string} [reportType] - Type of the report.
 * @property {boolean} [whiteLabelMode] - Indicates if the report is in white label mode.
 */
export interface IReportIntegrationSummary {
  companyId?: number;
  locationId?: number;
  companyName?: string;
  completedAt?: TISODateTime;
  reportType?: string;
  whiteLabelMode?: boolean;
}

/**
 * Vehicle details associated with the report.
 * @interface IReportIntegrationVehicle
 * @property {string} [displayName] - Display name of the vehicle (e.g. "2025 Ram 1500").
 * @property {string} [vin] - Vehicle Identification Number.
 * @property {string} [bodyType] - Body type of the vehicle.
 * @property {number} [miles] - Mileage of the vehicle.
 * @property {number} [adjustedValue] - Adjusted value of the vehicle.
 * @property {string} [engine] - Engine type or description.
 * @property {string} [transmission] - Transmission type.
 * @property {string} [trim] - Trim level of the vehicle.
 * @property {string|null} [licensePlate] - License plate number or null.
 * @property {string|null} [unitClientId] - Unit client identifier or null.
 */
export interface IReportIntegrationVehicle {
  displayName?: string;
  vin?: string;
  bodyType?: string;
  miles?: number;
  adjustedValue?: number;
  engine?: string;
  transmission?: string;
  trim?: string;
  licensePlate?: string | null;
  unitClientId?: string | null;
}

/**
 * Inspection notes for the report.
 * @interface IReportIntegrationNotes
 * @property {string|null} [inspection] - Inspection notes or null if not provided.
 */
export interface IReportIntegrationNotes {
  inspection?: string | null;
}

/**
 * Media files and related data for the report.
 * @interface IReportIntegrationMedia
 * @property {IUploadedFileDto|null} [signature] - Signature file or null.
 * @property {IUploadedFileDto|null} [estimationPdf] - Estimation PDF file or null.
 * @property {IReportIntegrationStepMedia[]} [steps] - Step media items.
 * @property {IReportIntegrationFrame[]} [frames360] - 360-degree frame items.
 */
export interface IReportIntegrationMedia {
  signature?: IUploadedFileDto | null;
  estimationPdf?: IUploadedFileDto | null;

  steps?: IReportIntegrationStepMedia[];
  frames360?: IReportIntegrationFrame[];
}

/**
 * Media information for a single inspection step.
 * @interface IReportIntegrationStepMedia
 * @property {string} type - Step type.
 * @property {string} category - Step category.
 * @property {string} name - Step name.
 * @property {IUploadedFileDto|null} [photo] - Photo file or null.
 * @property {string|null} [description] - Description or null.
 * @property {boolean} [inRedo] - Indicates if the step is in redo.
 */
export interface IReportIntegrationStepMedia {
  type: string;
  category: string;
  name: string;
  photo?: IUploadedFileDto | null;
  description?: string | null;
  inRedo?: boolean;
}

/**
 * Photo information for a 360-degree frame.
 * @interface IReportIntegrationFramePhoto
 * @property {number|null} parentFileId - ID of the parent file or null.
 * @property {string} pathFile - Path to the file.
 */
export interface IReportIntegrationFramePhoto {
  parentFileId: number | null;
  pathFile: string;
}

/**
 * 360-degree frame item containing photo information.
 * @interface IReportIntegrationFrame
 * @property {IReportIntegrationFramePhoto} photo - Photo details for the frame.
 */
export interface IReportIntegrationFrame {
  photo: IReportIntegrationFramePhoto;
}

/**
 * Wheel position codes.
 * - LF: Left Front
 * - RF: Right Front
 * - LR: Left Rear
 * - RR: Right Rear
 * - SPARE: Spare wheel
 */
export type TWheelPosition = 'LF' | 'RF' | 'LR' | 'RR' | 'SPARE';

/**
 * Wheel information for the report.
 * @interface IReportIntegrationWheels
 * @property {Partial<Record<TWheelPosition, IReportIntegrationWheelItem>>} items - Wheel items by position.
 */
export interface IReportIntegrationWheels {
  items: Partial<Record<TWheelPosition, IReportIntegrationWheelItem>>;
}

/**
 * Photos related to a wheel.
 * @interface IReportIntegrationWheelPhotos
 * @property {IUploadedFileDto|null} [wheel] - Photo of the wheel or null.
 * @property {IUploadedFileDto|null} [treadAngle] - Photo of the tread angle or null.
 */
export interface IReportIntegrationWheelPhotos {
  wheel?: IUploadedFileDto | null;
  treadAngle?: IUploadedFileDto | null;
}

/**
 * Details for a single wheel item.
 * @interface IReportIntegrationWheelItem
 * @property {IReportIntegrationTire|null} [tire] - Tire information or null.
 * @property {IReportIntegrationRim|null} [rim] - Rim information or null.
 * @property {IReportIntegrationWheelPhotos} [photos] - Associated photos.
 */
export interface IReportIntegrationWheelItem {
  tire?: IReportIntegrationTire | null;
  rim?: IReportIntegrationRim | null;
  photos?: IReportIntegrationWheelPhotos;
}

/**
 * Tire information for a wheel.
 * @interface IReportIntegrationTire
 * @property {string|null} [condition] - Tire condition or null.
 * @property {string|null} [tread] - Tread information or null.
 * @property {string|null} [brand] - Tire brand or null.
 * @property {string|null} [size] - Tire size or null.
 * @property {string|null} [dotTin] - DOT/TIN information or null.
 * @property {string|null} [type] - The type of the tire (e.g., P, LT, ST). Can be null if not applicable.
 */
export interface IReportIntegrationTire {
  condition?: string | null;
  tread?: string | null;
  brand?: string | null;
  size?: string | null;
  dotTin?: string | null;
  type?: string | null;
}

/**
 * Rim information for a wheel.
 * @interface IReportIntegrationRim
 * @property {string} [color] - Color of the rim.
 * @property {string} [status] - Status of the rim.
 * @property {number} [size] - Size of the rim.
 */
export interface IReportIntegrationRim {
  color?: string;
  status?: string;
  size?: number;
}

/**
 * Questionnaire answers for the report.
 * @interface IReportIntegrationQuestionnaire
 * @property {IReportIntegrationQuestionAnswer[]} items - List of question answers.
 */
export interface IReportIntegrationQuestionnaire {
  items: IReportIntegrationQuestionAnswer[];
}

/**
 * Single questionnaire answer.
 * @interface IReportIntegrationQuestionAnswer
 * @property {number} order - Order of the question.
 * @property {string} title - Title of the question.
 * @property {string} type - Type of the question.
 * @property {string[]} answer - Answers to the question.
 * @property {string|null} [comment] - Optional comment.
 * @property {IUploadedFileDto[]} photos - Associated photos.
 */
export interface IReportIntegrationQuestionAnswer {
  order: number;
  title: string;
  type: string;
  answer: string[];
  comment?: string | null;
  photos: IUploadedFileDto[];
}

/**
 * Damage items for the report.
 * @interface IReportIntegrationDamages
 * @property {IReportIntegrationDamageItem[]} items - List of damage items.
 */
export interface IReportIntegrationDamages {
  items: IReportIntegrationDamageItem[];
}

/**
 * Rule information for a damage item.
 * @interface IReportIntegrationDamageRule
 * @property {number} [damageRuleId] - ID of the damage rule.
 * @property {number} [mainPanelRuleId] - ID of the main panel rule.
 * @property {number} [subPanelRuleId] - ID of the sub panel rule.
 */
export interface IReportIntegrationDamageRule {
  damageRuleId?: number;
  mainPanelRuleId?: number;
  subPanelRuleId?: number;
}

/**
 * Domain information for a damage item.
 * @interface IReportIntegrationDamageDomain
 * @property {string} [type] - Type of the domain (e.g. Exterior/Wheels).
 * @property {string} [reportBlock] - Report block (e.g. Exterior/Rims).
 * @property {string} [panelName] - Name of the panel.
 * @property {string} [damageType] - Type of the damage.
 */
export interface IReportIntegrationDamageDomain {
  type?: string;
  reportBlock?: string;
  panelName?: string;
  damageType?: string;
}

/**
 * Part information for a damage estimate.
 * @interface IReportIntegrationDamageEstimatePart
 * @property {string} [name] - Name of the part.
 */
export interface IReportIntegrationDamageEstimatePart {
  name?: string;
}

/**
 * Amounts for a damage estimate.
 * @interface IReportIntegrationDamageEstimateAmounts
 * @property {string|number} [labor] - Labor cost or value.
 * @property {string|number} [parts] - Parts cost or value.
 * @property {string|number} [total] - Total cost or value.
 */
export interface IReportIntegrationDamageEstimateAmounts {
  labor?: string | number;
  parts?: string | number;
  total?: string | number;
}

/**
 * Damage estimate details.
 * @interface IReportIntegrationDamageEstimate
 * @property {IReportIntegrationDamageEstimatePart} [part] - Part information.
 * @property {IReportIntegrationDamageEstimateAmounts} [amounts] - Estimate amounts.
 */
export interface IReportIntegrationDamageEstimate {
  part?: IReportIntegrationDamageEstimatePart;
  amounts?: IReportIntegrationDamageEstimateAmounts;
}

export type TReportIntegrationDamageBillingCategory = 'CHARGEABLE' | 'DEDUCTIBLE';

/**
 * Billing math values for a damage item.
 * @interface IReportIntegrationDamageBilling
 * @property {DamageRuleBillingType} category - Chargeable when Claim is pressed, deductible otherwise.
 * @property {number} originalTotal - Original estimate total used for W&T Actual.
 * @property {number|null} newTotal - Adjusted total after waive/adjustment, null when unchanged.
 * @property {number} effectiveTotal - Total used after applying damage-level adjustment.
 * @property {number} waivedAmount - Original Total minus effective total.
 */
export interface IReportIntegrationDamageBilling {
  category: DamageRuleBillingType;
  originalTotal: number;
  newTotal: number | null;
  effectiveTotal: number;
  waivedAmount: number;
}

/**
 * Single damage item in the report.
 * @interface IReportIntegrationDamageItem
 * @property {number} id - Unique identifier for the damage item.
 * @property {string} [origin] - Origin of the damage.
 * @property {DamageRuleCondition} [condition] - Condition of the damage rule.
 * @property {IReportIntegrationDamageRule} [rule] - Rule information.
 * @property {IReportIntegrationDamageDomain} [domain] - Domain information.
 * @property {IUploadedFileDto|null} [photo] - Associated photo or null.
 * @property {IReportIntegrationDamageEstimate} [estimate] - Estimate details.
 * @property {boolean|null} [isClaimed] - Indicates if the damage is claimed or null.
 * @property {boolean|null} [isWaived] - Indicates if the damage is waived or null.
 * @property {IReportIntegrationDamageBilling} [billing] - Damage-level billing values.
 */
export interface IReportIntegrationDamageItem {
  id: number;
  origin?: string;
  condition?: DamageRuleCondition;
  rule?: IReportIntegrationDamageRule;
  domain?: IReportIntegrationDamageDomain;
  photo?: IUploadedFileDto | null;
  estimate?: IReportIntegrationDamageEstimate;
  isClaimed?: boolean | null;
  isWaived?: boolean | null;
  billing?: IReportIntegrationDamageBilling;
}

/**
 * Before/after comparison item for the report integration response.
 * @interface IReportIntegrationBeforeAfterComparison
 * @property {string|null} [pairId] - Comparable pair identifier.
 * @property {IReportIntegrationBeforeAfterDamage[]} [damages] - Detected damages for the compared pair.
 */
export interface IReportIntegrationBeforeAfterComparison {
  pairId?: string | null;
  damages?: IReportIntegrationBeforeAfterDamage[];
}

/**
 * Single AI damage item for a before/after comparison.
 * @interface IReportIntegrationBeforeAfterDamage
 * @property {string|null} [damageType] - Damage type.
 * @property {string|null} [carPanel] - Human-readable panel name.
 * @property {number|null} [panelId] - Panel rule identifier.
 * @property {string|null} [typePanel] - Technical panel key.
 * @property {string|null} [description] - Damage description.
 */
export interface IReportIntegrationBeforeAfterDamage {
  damageType?: string | null;
  carPanel?: string | null;
  panelId?: number | null;
  typePanel?: string | null;
  description?: string | null;
}

/**
 * Before/after block for the report integration response.
 * @interface IReportIntegrationBeforeAfter
 * @property {IReportIntegrationBeforeAfterComparison[]} comparisons - Comparison results saved for the contract.
 */
export interface IReportIntegrationBeforeAfter {
  comparisons: IReportIntegrationBeforeAfterComparison[];
}

/**
 * UV-map item derived from before/after comparisons.
 * @interface IReportIntegrationUvMapItem
 * @property {string} panel - Panel mask key.
 * @property {string} color - Color for UV-map rendering.
 * @property {string|null} [damageType] - Optional damage type for the panel.
 */
export interface IReportIntegrationUvMapItem {
  panel: string;
  color: string;
  damageType?: string | null;
}

/**
 * Visualization block for the report integration response.
 * @interface IReportIntegrationVisualization
 * @property {IReportIntegrationUvMapItem[]} [uvMap] - Optional UV-map payload derived from before/after comparisons.
 */
export interface IReportIntegrationVisualization {
  uvMap?: IReportIntegrationUvMapItem[];
}

/**
 * Estimation totals for the report.
 * @interface IReportIntegrationTotalsEstimation
 * @property {string|number|null} [total] - Total estimation value or null.
 */
export interface IReportIntegrationTotalsEstimation {
  total?: string | number | null;
}

/**
 * Leasing totals for the report.
 * @interface IReportIntegrationTotalsLeasing
 * @property {string|number} [total] - Total leasing value.
 * @property {string|number} [selfInsured] - Self-insured amount.
 * @property {string|number} [waived] - Waived amount.
 * @property {string} [initials] - Initials of the lessee.
 * @property {string|number} [billableToLessee] - Amount billable to the lessee.
 * @property {boolean} [isSaved] - Indicates if the leasing totals are saved.
 */
export interface IReportIntegrationTotalsLeasing {
  total?: string | number;
  selfInsured?: string | number;
  waived?: string | number;
  initials?: string;
  billableToLessee?: string | number;
  isSaved?: boolean;
}

/**
 * Totals and summary values for the report.
 * @interface IReportIntegrationTotals
 * @property {string|number|null} [crScore] - Condition report score.
 * @property {IReportIntegrationTotalsEstimation} [estimation] - Estimation totals.
 * @property {unknown|null} [historyReport] - History report data.
 * @property {IReportIntegrationTotalsLeasing} [leasing] - Leasing totals.
 */
export interface IReportIntegrationRequiresAttention {
  reasons: REPORTS_CONTRACT.RequiresAttentionReason[];
}

/**
 * Totals and summary values for the report.
 * @interface IReportIntegrationTotals
 * @property {string|number|null} [crScore] - Condition report score.
 * @property {IReportIntegrationTotalsEstimation} [estimation] - Estimation totals.
 * @property {unknown|null} [historyReport] - History report data.
 * @property {IReportIntegrationTotalsLeasing} [leasing] - Leasing totals.
 * @property {IReportIntegrationRequiresAttention|null} [requiresAttention] - Optional reasons that caused REQUIRES_ATTENTION status.
 * @property {IReportEstimateSummaryView|null} [estimateSummary] - Dealer CR v2 Estimate Summary (only for Dealer/Rental EST reports; requested via the estimateSummary include).
 */
export interface IReportIntegrationTotals {
  crScore?: string | number | null;
  estimation?: IReportIntegrationTotalsEstimation;
  historyReport?: unknown | null;
  leasing?: IReportIntegrationTotalsLeasing;
  requiresAttention?: IReportIntegrationRequiresAttention | null;
  estimateSummary?: IReportEstimateSummaryView | null;
}

/**
 * Metadata about the report integration response.
 * @interface IReportIntegrationMeta
 * @property {string} version - Version of the response format.
 * @property {TISODateTime} generatedAt - ISO 8601 datetime when the response was generated.
 */
export interface IReportIntegrationMeta {
  version: string;
  generatedAt: TISODateTime;
}
